import { Injectable } from '@angular/core';
import { PcnModel } from './shared/common/viewmodels/pcn.model';
import { ReasonModel } from './shared/common/viewmodels/reason.model';
import { QuestionAndAnswerModel } from './shared/common/viewmodels/questionAndAnswer.model';
import { ReasonService } from './shared/common/services/reason.service';
import { PcnBlockService } from './shared/common/services/pcn-block.service';
import { AlertModel } from './shared/common/viewmodels/alert.model';
import { QuestionService } from './shared/common/services/question.service';
import { SettingsService } from './authentication/services/settings.service';
import { AdminService } from './layouts/services/admin.service';
import { StorageService } from './shared/common/services/storage.service';
import { BehaviorSubject } from 'rxjs';

@Injectable()
export class Global {
  public PCN: PcnModel = this.pcnBlockService.getLocalPcn();
  public questionsAndAnswers: Array<QuestionAndAnswerModel> =
    this.questionsService.getLocalQuestionsAndAnswers();
  // Questions the user has clicked or changed in the current case. Shared so the incremental audit
  // (documents-questions) and the final save (nexus-upload) report the same set, and so it survives
  // documents-questions being destroyed and recreated. Cleared wherever questionsAndAnswers is reset
  // for a new case; never on a new walk or upload within the same case.
  public touchedQuestionIds: Set<number> = new Set<number>();
  public offencesTypes: Array<any> = this.adminService.getLocalOffences();
  public questionsObjectList: Array<any> = [];
  public customLetters: Array<AlertModel> = [];
  public customParagraphs: Array<AlertModel> = [];
  public alerts: Array<AlertModel> = [];
  public codeErrors: Array<any> = [];
  public decisionActions: any = this.questionsService.getLocalDecisionActions();
  public questionTypes: any = this.questionsService.getLocalQuestionTypes();
  public questionClasses: any = this.questionsService.getLocalQuestionClasses();
  public policiesAnswers: any = this.questionsService.getLocalPoliciesAnswers();
  public isPcnClosed: boolean = this.pcnBlockService.isPcnClosed();

  public showPcnExtraFields = false;
  public isTokenValid = false;
  public tokenPcn: PcnModel = new PcnModel();
  public pcnNotComplete = false;
  public encryptedToken = '';
  public isTokenPcnActive = false;
  public interfaceTokenData = '';
  public clientDomain = '';
  public RmOnline = false;
  // Shared with WelcomeLayoutComponent (which owns the loading spinner on /pcn-details) so
  // pcn-block.component.ts can clear it when it needs to keep the user on this page - e.g. to show
  // the Fine Now confirmation modal - rather than navigating away as the success path does. Must
  // live here rather than as a local field on WelcomeLayoutComponent, since pcn-block.component.ts
  // has no direct reference to its parent to reset it on otherwise.
  public pcnPrepopulateLoading = false;

  /* PCN Panel Locking */
  public isPcnPanelLocked: boolean = this.pcnBlockService.checkPcnPanelLocked();
  public isPcnPinVisible = true;
  public pcnPanelLocked$ = new BehaviorSubject<boolean>(this.isPcnPanelLocked);

  constructor(
    public reasonService: ReasonService,
    public pcnBlockService: PcnBlockService,
    public questionsService: QuestionService,
    public settingsService: SettingsService,
    private adminService: AdminService,
    private storageService: StorageService
  ) {}

  get SELECTED_REASONS(): Array<ReasonModel> {
    return this.reasonService.getLocalReasons();
  }

  get isReasonsLocked(): boolean {
    return this.storageService.isReasonsLocked();
  }
  get isLetterTopLocked(): boolean {
    return this.storageService.isLetterTopLocked();
  }
  get isPcnDetailsLocked(): boolean {
    return this.storageService.isPcnDetailsLocked();
  }
  get isPcnProcessing(): boolean {
    return this.storageService.isPcnProcessing();
  }

  set isReasonsLocked(value: boolean) {
    this.storageService.lockReasons(value);
  }
  set isLetterTopLocked(value: boolean) {
    this.storageService.lockLetterTop(value);
  }
  set isPcnDetailsLocked(value: boolean) {
    this.storageService.lockPcnDetails(value);
  }
  set isPcnProcessing(value: boolean) {
    this.storageService.setPcnProcessing(value);
  }

  resetSelectedReasons() {
    this.storageService.clearSessionStorageItem('rm_reasons');
  }

  setPcnPanelLocked(lock: boolean) {
    this.isPcnPanelLocked = lock;
    this.pcnPanelLocked$.next(lock); // Notify all subscribers
  }
}
